import { FrequencyUnit, Task, Timestamp } from '@griply/types';
import {
  createPlannedOccurrenceForDate,
  deletePlannedOccurrenceForDate,
  getScheduleByDate,
} from '@griply/utils';
import { getDaysInMonth, getDaysInYear } from 'date-fns';
import { useUpdateTask } from 'features/tasks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToday } from 'shared/contexts/today';

import { useData } from '../../use-data';
import { UpcomingPeriod, UpcomingPeriodTypes } from '../upcoming/types';

export type UsePeriodOccurrencesOptions = {
  task: Task;
  period: UpcomingPeriod;
  targetCount: number;
  plannedCount: number;
};

export const usePeriodOccurrences = ({
  task,
  period,
  targetCount,
  plannedCount,
}: UsePeriodOccurrencesOptions) => {
  const { t } = useTranslation();
  const today = useToday();
  const { weekStartsOn } = useData();

  const { submit: updateTask } = useUpdateTask();

  const onAddExtraOccurrence = (date: Timestamp | null) => {
    if (!task.schedules?.length || !date) {
      return;
    }

    const schedules = createPlannedOccurrenceForDate(task.schedules, date, {
      today,
      weekStartsOn,
    });

    updateTask({ id: task.id, schedules });
  };

  const onRemoveExtraOccurrence = (date: Timestamp | null) => {
    if (!task.schedules?.length || !date) {
      return;
    }

    const activeSchedule = getScheduleByDate(task.schedules, { date, today });

    if (!activeSchedule) {
      return;
    }

    const updatedSchedule = deletePlannedOccurrenceForDate(
      activeSchedule,
      date,
      {
        today,
        weekStartsOn,
      },
    );

    const schedules = task.schedules.map((schedule) => {
      if (
        schedule.startDate.getTime() === updatedSchedule.startDate.getTime()
      ) {
        return updatedSchedule;
      }
      return schedule;
    });

    updateTask({ id: task.id, schedules });
  };

  const showExtraOccurrenceButton = useMemo(() => {
    if (period.type === UpcomingPeriodTypes.Overdue) {
      return false;
    }

    const maxDays =
      period.type === FrequencyUnit.Month
        ? getDaysInMonth(period.date)
        : period.type === FrequencyUnit.Year
          ? getDaysInYear(period.date)
          : 7;

    return (
      period.days.length < maxDays || period.days.some((day) => !day.entry)
    );
  }, [period.days, period.date, period.type]);

  const addOccurrenceLabel = useMemo(() => {
    if (targetCount && !plannedCount) {
      const activeSchedule = getScheduleByDate(task.schedules, {
        date: period.date,
        today,
      });
      if (activeSchedule?.frequency?.unit === FrequencyUnit.Week) {
        return t('context-menus.task.labels.plan-occurrence');
      }
    }
    return t('context-menus.task.labels.extra-occurrence');
  }, [targetCount, plannedCount, t, task.schedules, period.date, today]);

  return {
    onAddExtraOccurrence,
    onRemoveExtraOccurrence,
    showExtraOccurrenceButton,
    addOccurrenceLabel,
  };
};
